import { useEffect, useRef, useState } from "react";
import { Upload, Link2, Trash2, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { useConfig } from "../../context/BrandContext";
import { Card, TextInput, Btn, run } from "./AdminUI";

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml,.svg";
const MAX = 5 * 1024 * 1024;

export const BrandingCard = () => {
  const { reload } = useConfig();
  const [d, setD] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { adminApi.get("/admin/settings/branding").then((r) => setD(r.data)); }, []);

  const done = async (r) => { if (r) { setD(r.data); await reload(); } setBusy(false); setArmed(false); };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) return run(() => Promise.reject({ response: { data: { detail: "Logo must be 5 MB or smaller." } } }));
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    await done(await run(() => adminApi.post("/admin/settings/branding/logo", fd), "Logo uploaded"));
    if (fileRef.current) fileRef.current.value = "";
  };
  const fromUrl = async () => {
    setBusy(true);
    const r = await run(() => adminApi.post("/admin/settings/branding/logo-url", { url }), "Logo saved from URL");
    if (r) setUrl("");
    await done(r);
  };
  const remove = async () => { setBusy(true); await done(await run(() => adminApi.delete("/admin/settings/branding/logo"), "Logo removed")); };

  return (
    <Card title="Branding" testid="branding-settings">
      {!d ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="grid md:grid-cols-[200px_1fr] gap-8 items-start">
          <div className="text-center">
            {d.logo_url ? (
              <>
                <img src={d.logo_url} alt="Current logo" className="w-40 h-40 mx-auto rounded-full object-cover border border-slate-200" data-testid="branding-preview" />
                <div className="mt-3 flex items-center justify-center gap-2">
                  <img src={d.logo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                  {d.favicon_url && <img src={d.favicon_url} alt="Favicon preview" className="w-4 h-4" data-testid="branding-favicon-preview" />}
                </div>
                <p className="mt-2 text-xs text-slate-500 break-all" data-testid="branding-meta">{d.logo.filename} · {(d.logo.size / 1024).toFixed(0)} KB · {d.logo.source}</p>
              </>
            ) : (
              <div className="w-40 h-40 mx-auto rounded-full border-2 border-dashed border-slate-300 grid place-items-center text-xs text-slate-500" data-testid="branding-empty">No logo yet.<br />Text wordmark shown.</div>
            )}
          </div>
          <div className="space-y-5">
            <div>
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="branding-file-input" />
              <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="branding-upload-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{d.logo_url ? "Replace logo" : "Upload logo"}
              </Btn>
              <p className="mt-2 text-xs text-slate-500">PNG, JPG, WEBP or SVG, max 5 MB. Square images work best; the logo is shown cropped to a circle. Favicon and app icon are generated automatically (raster images).</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <TextInput label="Or paste an image URL (https://)" value={url} onChange={setUrl} testid="branding-url-input" className="flex-1" placeholder="https://…/logo.png" />
              <Btn variant="ghost" onClick={fromUrl} disabled={busy || !url.trim()} data-testid="branding-url-button"><Link2 className="w-4 h-4" />Use URL</Btn>
            </div>
            {d.logo_url && (
              <div className="flex gap-2">
                {armed && <Btn variant="ghost" onClick={() => setArmed(false)} data-testid="branding-remove-cancel">Keep logo</Btn>}
                <Btn variant="danger" disabled={busy} onClick={armed ? remove : () => setArmed(true)} data-testid={armed ? "branding-remove-confirm" : "branding-remove-button"}>
                  <Trash2 className="w-4 h-4" />{armed ? "Confirm remove" : "Remove logo"}
                </Btn>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
